/**
 * Enhanced authentication utility functions with role-based access control
 */

/**
 * Get the current user from localStorage
 * @returns {Object|null} The current user object or null if not authenticated
 */
export const getCurrentUser = () => {
  try {
    const userString = localStorage.getItem('user');
    return userString ? JSON.parse(userString) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Save user data to localStorage
 * @param {Object} userData - The user data to save
 */
export const saveUserData = (userData) => {
  try {
    localStorage.setItem('user', JSON.stringify(userData));
    if (userData.token) {
      localStorage.setItem('token', userData.token);
    }
    if (userData.role) {
      localStorage.setItem('role', userData.role);
    }
  } catch (error) {
    console.error('Error saving user data:', error);
  }
};

/**
 * Get the current user's role
 * @returns {string|null} The user's role or null if not authenticated
 */
export const getUserRole = () => {
  const user = getCurrentUser();
  const pressUser = localStorage.getItem('pressUser');
  const userRole = localStorage.getItem('userRole');
  
  if (user?.role) return user.role;
  if (userRole) return userRole;
  if (pressUser) {
    try {
      const parsedPressUser = JSON.parse(pressUser);
      return parsedPressUser.role || 'press';
    } catch (error) {
      console.error('Error parsing press user data:', error);
    }
  }
  return null;
};

/**
 * Check if the current user has the required role
 * @param {string|Array} requiredRoles - The role or array of roles to check against
 * @returns {boolean} True if user has one of the required roles, false otherwise
 */
export const hasRole = (requiredRoles) => {
  const userRole = getUserRole();
  if (!userRole) return false;
  
  // Convert requiredRoles to array if it's a string
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  
  // Check if user's role matches any of the required roles
  return roles.some(role => userRole.toLowerCase() === role.toLowerCase());
};

/**
 * Check if the user is authenticated
 * @returns {boolean} True if user is authenticated, false otherwise
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const pressToken = localStorage.getItem('pressToken');
  return !!(token || pressToken);
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('role');
  localStorage.removeItem('pressToken');
  localStorage.removeItem('pressUser');
  localStorage.removeItem('userRole');
  localStorage.removeItem('redirectPath');
};

/**
 * Get the authorization header for API requests
 * @returns {Object} The headers object with Authorization
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const pressToken = localStorage.getItem('pressToken');
  const authToken = token || pressToken;
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
};

/**
 * Redirect to login page based on user role
 * @param {string} role - The user role
 * @param {function} navigate - The navigation function from react-router
 */
export const redirectToLogin = (role, navigate) => {
  const loginPaths = {
    superadmin: '/admin/login',
    subadmin: '/admin/login',
    reporter: '/login',
    advertiser: '/login',
    influencer: '/login',
    ryvuser: '/raise/login',
    press: '/press/login',
    advocate: '/advocate/login',
    default: '/login'
  };
  
  const path = loginPaths[role?.toLowerCase()] || loginPaths.default;
  navigate(path, { replace: true });
};

/**
 * Redirect to dashboard based on user role
 * @param {string} role - The user role
 * @param {function} navigate - The navigation function from react-router
 * @param {string} customPath - Optional custom path to redirect to
 */
export const redirectToDashboard = (role, navigate, customPath = null) => {
  if (customPath) {
    navigate(customPath, { replace: true });
    return;
  }
  
  const dashboardPaths = {
    superadmin: '/admin/dashboard',
    subadmin: '/admin/dashboard',
    reporter: '/reporter/dashboard',
    advertiser: '/advertiser/dashboard',
    influencer: '/influencer/dashboard',
    ryvuser: '/raise/dashboard',
    press: '/press/dashboard',
    advocate: '/advocate/dashboard',
    default: '/'
  };
  
  const path = dashboardPaths[role?.toLowerCase()] || dashboardPaths.default;
  navigate(path, { replace: true });
};

/**
 * Check if the current path is accessible for the user's role
 * @param {string} path - The path to check
 * @returns {boolean} True if the path is accessible, false otherwise
 */
export const isPathAccessible = (path) => {
  const role = getUserRole();
  if (!role) return false;
  
  // Define path access rules by role
  const accessRules = {
    superadmin: ['/admin'],
    subadmin: ['/admin'],
    reporter: ['/reporter'],
    advertiser: ['/advertiser'],
    influencer: ['/influencer'],
    ryvuser: ['/raise'],
    press: ['/press'],
    advocate: ['/advocate']
  };
  
  const allowedPaths = accessRules[role.toLowerCase()] || [];
  
  // Check if the path starts with any of the allowed paths
  return allowedPaths.some(allowedPath => path.startsWith(allowedPath));
};
